import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

/**
 * WS1.7 (pre-deploy program 2026-08-11, Fredrik's walk ask) — @mention
 * authoring + display support.
 *
 *  - <MentionTextarea/>: a textarea that opens a filterable team-member
 *    dropdown when the user types `@`. Keyboard nav (up/down/enter/tab/
 *    escape) + click select. On select the partial token is replaced with
 *    the member's dotted display name (`@Firstname.Lastname`) — the exact
 *    form the backend mentionResolverService rule-2 lookup resolves, so
 *    a picked mention can't typo-miss.
 *
 *  - renderWithMentions(): renders a comment body with @tokens as pills.
 *    Tokens that match a resolved entry in comment.mentionedTeamMembers
 *    render solid blue; tokens that DIDN'T resolve render dashed amber —
 *    making typos visible after the fact (walk feedback: "the user cannot
 *    verify a typo didn't occur").
 *
 * Handle grammar mirrors backend HANDLE_RE: contiguous [A-Za-z0-9._+-]
 * after `@` at start-of-string / whitespace / '('.
 */

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';

export interface MentionableMember {
  _id: string;
  name: string;
  email: string;
}

// Module-level team cache (5 min) — same pattern as useCurrentUserStep.
const TEAM_TTL_MS = 5 * 60_000;
let _teamCache: { data: MentionableMember[]; fetchedAt: number } | null = null;
let _teamInflight: Promise<MentionableMember[]> | null = null;

async function fetchTeam(
  getAuthHeaders: () => Promise<Record<string, string>>
): Promise<MentionableMember[]> {
  if (_teamCache && Date.now() - _teamCache.fetchedAt < TEAM_TTL_MS) return _teamCache.data;
  if (_teamInflight) return _teamInflight;
  _teamInflight = (async () => {
    try {
      const headers = await getAuthHeaders();
      const res = await axios.get<MentionableMember[]>(`${backendUrl}/api/team`, { headers });
      const data = (res.data || []).filter(m => m.name && m.email);
      _teamCache = { data, fetchedAt: Date.now() };
      return data;
    } catch {
      return [];
    } finally {
      _teamInflight = null;
    }
  })();
  return _teamInflight;
}

/** "Michael Luxemburg (DEV)" → "Michael.Luxemburg" — backend rule-2 form. */
export function dottedHandle(name: string): string {
  return name.replace(/\s*\([^)]*\)/g, '').trim().replace(/\s+/g, '.');
}

/** All lowercase forms a member's handle could legally take (backend rules 1-3). */
function handleForms(m: MentionableMember): string[] {
  const forms = [
    (m.email || '').split('@')[0],
    dottedHandle(m.name || ''),
    (m.name || '').trim().split(/\s+/)[0]
  ];
  return forms.filter(Boolean).map(f => f.toLowerCase());
}

// ---------------------------------------------------------------------------
// MentionTextarea
// ---------------------------------------------------------------------------

interface MentionTextareaProps {
  value: string;
  onChange: (value: string) => void;
  getAuthHeaders: () => Promise<Record<string, string>>;
  placeholder?: string;
  rows?: number;
  disabled?: boolean;
  // 2026-08-27 (Legal reviewer, live video cycle) — the caret has to land in
  // the field the moment the composer reveals it; he was clicking into it by
  // hand on every comment.
  autoFocus?: boolean;
}

// Active token = `@partial` immediately before the caret, preceded by
// start / whitespace / '('.
const ACTIVE_TOKEN_RE = /(^|[\s(])@([A-Za-z0-9._+-]*)$/;

export const MentionTextarea: React.FC<MentionTextareaProps> = ({
  value, onChange, getAuthHeaders, placeholder, rows = 3, disabled = false, autoFocus = false
}) => {
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  const [team, setTeam] = useState<MentionableMember[]>([]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [tokenStart, setTokenStart] = useState<number | null>(null); // index of '@'
  const [highlight, setHighlight] = useState(0);

  useEffect(() => {
    let cancelled = false;
    fetchTeam(getAuthHeaders).then(t => { if (!cancelled) setTeam(t); });
    return () => { cancelled = true; };
  }, [getAuthHeaders]);

  const matches = open
    ? team.filter(m => {
        const q = query.toLowerCase();
        if (!q) return true;
        return handleForms(m).some(f => f.startsWith(q)) ||
               (m.name || '').toLowerCase().includes(q);
      }).slice(0, 8)
    : [];

  const detectToken = useCallback((el: HTMLTextAreaElement) => {
    const uptoCaret = el.value.slice(0, el.selectionStart ?? el.value.length);
    const m = ACTIVE_TOKEN_RE.exec(uptoCaret);
    if (m) {
      setOpen(true);
      setQuery(m[2]);
      setTokenStart(uptoCaret.length - m[2].length - 1);
      setHighlight(0);
    } else {
      setOpen(false);
      setTokenStart(null);
    }
  }, []);

  function select(member: MentionableMember) {
    const el = taRef.current;
    if (!el || tokenStart === null) return;
    const caret = el.selectionStart ?? value.length;
    const inserted = `@${dottedHandle(member.name)} `;
    const next = value.slice(0, tokenStart) + inserted + value.slice(caret);
    onChange(next);
    setOpen(false);
    setTokenStart(null);
    // Restore focus + caret after React re-renders the value.
    const newCaret = tokenStart + inserted.length;
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(newCaret, newCaret);
    });
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (!open || matches.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => (h + 1) % matches.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => (h - 1 + matches.length) % matches.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      select(matches[highlight]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <div style={{ position: 'relative' }}>
      <textarea
        ref={taRef}
        value={value}
        onChange={e => { onChange(e.target.value); detectToken(e.target); }}
        onKeyDown={onKeyDown}
        onClick={e => detectToken(e.currentTarget)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
        autoFocus={autoFocus}
        data-testid="mention-textarea"
      />
      {open && matches.length > 0 && (
        <ul
          role="listbox"
          data-testid="mention-dropdown"
          style={{
            position: 'absolute', left: 0, right: 0, top: '100%', zIndex: 30,
            margin: '2px 0 0', padding: 4, listStyle: 'none',
            background: '#fff', border: '1px solid #ced4da', borderRadius: 6,
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto'
          }}
        >
          {matches.map((m, i) => (
            <li
              key={m._id}
              role="option"
              aria-selected={i === highlight}
              // onMouseDown (not onClick) so it fires before the textarea blur.
              onMouseDown={e => { e.preventDefault(); select(m); }}
              onMouseEnter={() => setHighlight(i)}
              style={{
                padding: '6px 10px', borderRadius: 4, cursor: 'pointer',
                background: i === highlight ? '#e8f0fa' : 'transparent',
                display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 13
              }}
            >
              <span style={{ fontWeight: 600, color: '#123250' }}>{m.name}</span>
              <span style={{ color: '#888', fontSize: 12 }}>@{dottedHandle(m.name)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// renderWithMentions
// ---------------------------------------------------------------------------

export interface MentionedRef {
  teamMemberId?: string | null;
  name?: string | null;
  email?: string | null;
}

// Global-flavored copy of backend HANDLE_RE for display parsing.
const DISPLAY_TOKEN_RE = /(^|[\s(])@([A-Za-z0-9._+-]+)/g;
const TRAILING_PUNCT = /[.,;:!?]+$/;

/**
 * Render a comment body with @tokens as pills. `mentioned` is the
 * comment's persisted mentionedTeamMembers array — tokens matching a
 * resolved entry render solid; unmatched tokens render dashed amber
 * (typo affordance).
 */
export function renderWithMentions(
  text: string,
  mentioned?: MentionedRef[] | null
): React.ReactNode {
  if (!text) return text;
  const resolvedForms = new Set<string>();
  for (const m of mentioned || []) {
    if (!m?.teamMemberId) continue;
    if (m.email) resolvedForms.add(m.email.split('@')[0].toLowerCase());
    if (m.name) {
      resolvedForms.add(dottedHandle(m.name).toLowerCase());
      resolvedForms.add(m.name.trim().split(/\s+/)[0].toLowerCase());
    }
  }

  const nodes: React.ReactNode[] = [];
  let last = 0;
  let key = 0;
  DISPLAY_TOKEN_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = DISPLAY_TOKEN_RE.exec(text)) !== null) {
    const prefix = m[1];
    const rawHandle = m[2].replace(TRAILING_PUNCT, '');
    if (!rawHandle) continue;
    const tokenStart = m.index + prefix.length;
    const tokenEnd = tokenStart + 1 + rawHandle.length; // '@' + handle
    if (tokenStart > last) nodes.push(text.slice(last, tokenStart));
    const isResolved = resolvedForms.has(rawHandle.toLowerCase());
    nodes.push(
      <span
        key={`mention-${key++}`}
        title={isResolved ? 'Mentioned team member (notified)' : `No team member matched "@${rawHandle}" — check the spelling`}
        style={isResolved
          ? { background: '#e8f0fa', color: '#123250', fontWeight: 600, padding: '0 5px', borderRadius: 4 }
          : { background: '#fff3cd', color: '#856404', fontWeight: 600, padding: '0 5px', borderRadius: 4, border: '1px dashed #ffc107' }}
      >
        @{rawHandle}
      </span>
    );
    last = tokenEnd;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes.length > 0 ? <>{nodes}</> : text;
}

/**
 * Does this comment @mention the person reading it?
 *
 * Reads the backend-resolved mentionedTeamMembers rather than re-parsing the
 * text: the resolver already decided which tokens matched a real person, so a
 * typo'd "@Rta" cannot masquerade as a mention of Rita here.
 *
 * Lives here rather than in ReviewInterface because DocumentViewer needs it
 * too (to flag screenshot overlays), and DocumentViewer is imported BY
 * ReviewInterface — the direct import made a cycle, which type-checks and
 * builds but can leave the binding undefined at module-evaluation time.
 */
export function mentionsViewer(
  comment: { mentionedTeamMembers?: MentionedRef[] | null },
  viewerEmail?: string | null
): boolean {
  const me = (viewerEmail || '').toLowerCase();
  if (!me) return false;
  return (comment.mentionedTeamMembers || []).some(
    m => !!m?.teamMemberId && (m.email || '').toLowerCase() === me
  );
}

/**
 * One decision for every comment-marker renderer: does this marker get the
 * mention treatment, and which comment does clicking it open?
 *
 * Cluster semantics on purpose — a marker often represents several
 * overlapping comments, and if ANY unresolved one mentions the viewer, the
 * marker must say so. Before this helper, DocumentViewer computed forMe on
 * the single comment while SnapshotViewer checked the whole cluster: the
 * same marker could flag in prod and not in the other path. Four renderers
 * draw markers (DocumentViewer screenshot / image / video-timeline,
 * SnapshotViewer); all of them call this.
 */
export function markerMentionProps(
  cluster: Array<{ _id: string; resolved?: boolean; mentionedTeamMembers?: MentionedRef[] | null }>,
  viewerEmail?: string | null
): { forMe: boolean; primaryId: string | null } {
  const forMe = cluster.some(c => !c.resolved && mentionsViewer(c, viewerEmail));
  return { forMe, primaryId: cluster[0]?._id || null };
}
